(() => {
  if (window.TabsWithContentLoaded) return;
  window.TabsWithContentLoaded = true;

  const SELECTORS = {
    section: '[data-tabs-with-content]',
    tabsShell: '[data-tabs-shell]',
    tabScroll: '[data-tabs-scroll]',
    tab: '[data-tabs-trigger]',
    panel: '[data-tabs-panel]',
    arrow: '[data-tabs-arrow]'
  };

  function safeEscape(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') {
      return window.CSS.escape(value);
    }

    return String(value).replace(/[^a-zA-Z0-9_-]/g, '\\$&');
  }

  function initTabsWithContent(section) {
    if (!section || section.dataset.tabsInitialized === 'true') return;

    section.dataset.tabsInitialized = 'true';

    const tabsShell = section.querySelector(SELECTORS.tabsShell);
    const tabScroll = section.querySelector(SELECTORS.tabScroll);
    const tabs = Array.from(section.querySelectorAll(SELECTORS.tab));
    const panels = Array.from(section.querySelectorAll(SELECTORS.panel));
    const arrows = Array.from(section.querySelectorAll(SELECTORS.arrow));

    if (!tabs.length || !panels.length) return;

    let resizeObserver = null;

    function updateArrows() {
      if (!tabScroll || !tabsShell || !arrows.length) return;

      const maxScrollLeft = tabScroll.scrollWidth - tabScroll.clientWidth;
      const isScrollable = maxScrollLeft > 2;

      tabsShell.classList.toggle('is-not-scrollable', !isScrollable);
      tabsShell.classList.toggle('is-scrollable', isScrollable);

      arrows.forEach((arrow) => {
        if (!isScrollable) {
          arrow.setAttribute('disabled', 'disabled');
        } else {
          arrow.removeAttribute('disabled');
        }
      });
    }

    function setActiveTab(activeTab, shouldFocus = false, shouldScroll = true) {
      if (!activeTab) return;

      const targetId = activeTab.getAttribute('data-target');
      if (!targetId) return;

      const targetPanel = section.querySelector(`#${safeEscape(targetId)}`);
      if (!targetPanel) return;

      tabs.forEach((tab) => {
        const isActive = tab === activeTab;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.setAttribute('tabindex', isActive ? '0' : '-1');
      });

      panels.forEach((panel) => {
        const isActive = panel === targetPanel;
        panel.classList.toggle('is-active', isActive);

        if (isActive) {
          panel.removeAttribute('hidden');
        } else {
          panel.setAttribute('hidden', '');
        }
      });

      if (shouldScroll) {
        activeTab.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      }

      if (shouldFocus) activeTab.focus();

      updateArrows();
    }

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => {
        setActiveTab(tab);
      });

      tab.addEventListener('keydown', (event) => {
        const key = event.key;

        if (key === 'Enter' || key === ' ') {
          event.preventDefault();
          setActiveTab(tab, true);
        }

        if (key === 'ArrowRight') {
          event.preventDefault();
          const nextTab = tabs[index + 1] || tabs[0];
          setActiveTab(nextTab, true);
        }

        if (key === 'ArrowLeft') {
          event.preventDefault();
          const previousTab = tabs[index - 1] || tabs[tabs.length - 1];
          setActiveTab(previousTab, true);
        }

        if (key === 'Home') {
          event.preventDefault();
          setActiveTab(tabs[0], true);
        }

        if (key === 'End') {
          event.preventDefault();
          setActiveTab(tabs[tabs.length - 1], true);
        }
      });
    });

    arrows.forEach((arrow) => {
      arrow.addEventListener('click', () => {
        if (!tabScroll || arrow.hasAttribute('disabled')) return;

        const direction = arrow.getAttribute('data-tabs-arrow');
        const maxScrollLeft = tabScroll.scrollWidth - tabScroll.clientWidth;
        const currentScrollLeft = tabScroll.scrollLeft;
        const scrollAmount = Math.max(tabScroll.clientWidth * 0.7, 280);

        if (direction === 'next') {
          if (currentScrollLeft >= maxScrollLeft - 2) {
            tabScroll.scrollTo({
              left: 0,
              behavior: 'smooth'
            });
          } else {
            tabScroll.scrollBy({
              left: scrollAmount,
              behavior: 'smooth'
            });
          }
        }

        if (direction === 'prev') {
          if (currentScrollLeft <= 2) {
            tabScroll.scrollTo({
              left: maxScrollLeft,
              behavior: 'smooth'
            });
          } else {
            tabScroll.scrollBy({
              left: -scrollAmount,
              behavior: 'smooth'
            });
          }
        }
      });
    });

    if (tabScroll) {
      tabScroll.addEventListener('scroll', updateArrows, { passive: true });

      if ('ResizeObserver' in window) {
        resizeObserver = new ResizeObserver(updateArrows);
        resizeObserver.observe(tabScroll);
      } else {
        window.addEventListener('resize', updateArrows);
      }
    }

    const activeTab = tabs.find((tab) => tab.classList.contains('is-active')) || tabs[0];
    setActiveTab(activeTab, false, false);

    if (tabScroll) {
      tabScroll.scrollLeft = 0;
    }

    window.requestAnimationFrame(updateArrows);
    window.setTimeout(updateArrows, 300);

    section.tabsWithContentDestroy = function destroyTabsWithContent() {
      if (resizeObserver) {
        resizeObserver.disconnect();
      } else {
        window.removeEventListener('resize', updateArrows);
      }

      section.dataset.tabsInitialized = 'false';
    };
  }

  function initAllTabsWithContent(root = document) {
    root.querySelectorAll(SELECTORS.section).forEach(initTabsWithContent);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initAllTabsWithContent();
    });
  } else {
    initAllTabsWithContent();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector(SELECTORS.section);

    if (section) {
      section.dataset.tabsInitialized = 'false';
      initTabsWithContent(section);
      return;
    }

    if (event.target.matches && event.target.matches(SELECTORS.section)) {
      event.target.dataset.tabsInitialized = 'false';
      initTabsWithContent(event.target);
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.querySelector(SELECTORS.section) || event.target;

    if (section && typeof section.tabsWithContentDestroy === 'function') {
      section.tabsWithContentDestroy();
    }
  });
})();